import { z } from 'zod';

export const domains = [
  'software',
  'design',
  'product',
  'marketing',
  'sales',
  'finance',
  'legal',
  'people',
  'operations',
  'data',
  'security',
  'research',
] as const;
export const domainSchema = z.enum(domains);
export const tierSchema = z.enum(['lightweight', 'standard', 'advanced']);
export const competencySchema = z.object({
  own: z.array(domainSchema).min(1),
  contribute: z.array(domainSchema),
  defer: z.array(domainSchema).min(1),
});
export const personaSchema = z.object({
  profession: z.string(),
  headline: z.string(),
  specialisms: z.array(z.string()).min(2).max(5),
  adjacent: z.array(z.string()),
  contract: competencySchema,
  businessTypes: z.array(z.string()),
  stages: z.array(z.string()),
  roles: z.array(z.string()),
  level: z.enum(['Experienced', 'Senior', 'Principal']),
  communication: z.string(),
  traits: z.array(z.string()),
  workingStyle: z.string(),
  risk: z.enum(['Cautious', 'Balanced', 'Experimental']),
  availabilityStyle: z.string(),
  description: z.string(),
  tags: z.array(z.string()),
  availability: z.enum(['available', 'busy']),
  workload: z.object({ conversations: z.number(), projects: z.number() }),
  seed: z.number(),
  version: z.literal(1),
});
export const assetSchema = z.object({
  path: z
    .string()
    .regex(
      /^assets\/(?:[a-zA-Z0-9_-]+\/)*[a-zA-Z0-9_-]+\.(?:svg|png|jpg|jpeg|webp|gif|txt|csv|json|woff2)$/,
    ),
  content: z.string().max(1_000_000),
  encoding: z.enum(['utf8', 'base64']),
});
export const websiteSchema = z.object({
  html: z.string().max(250_000),
  css: z.string().max(150_000),
  js: z.string().max(150_000),
  assets: z.array(assetSchema).max(30).default([]),
});
export type Website = z.infer<typeof websiteSchema>;
export type Persona = z.infer<typeof personaSchema>;
export type Domain = z.infer<typeof domainSchema>;
export type Tier = z.infer<typeof tierSchema>;
export const taskSchema = z.object({
  id: z.string(),
  projectId: z.string().optional(),
  requestedByUserId: z.string(),
  collaboratorId: z.string(),
  channel: z.string(),
  description: z.string(),
  domain: domainSchema.optional(),
  type: z.enum(['conversation', 'implementation', 'analysis', 'writing', 'planning']),
  status: z.enum([
    'queued',
    'analysing',
    'working',
    'completed',
    'failed',
    'cancelled',
    'awaiting_clarification',
  ]),
  initialTier: tierSchema.default('lightweight'),
  modelTier: tierSchema.default('lightweight'),
  createdAt: z.string(),
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
  result: z.string().optional(),
  artifactId: z.string().optional(),
  error: z.string().optional(),
  requestCount: z.number().default(0),
  retries: z.number().default(0),
  inputTokens: z.number().default(0),
  outputTokens: z.number().default(0),
  model: z.string().optional(),
  latencyMs: z.number().default(0),
  escalationReason: z.string().optional(),
});
export type AITask = z.infer<typeof taskSchema>;
